import { useId, useState, type KeyboardEvent } from "react";
import { MAX_GENRES, POPULAR_GENRES, matchGenres } from "./genres.js";
import styles from "./ApplyPage.module.scss";

export interface GenrePickerProps {
  value: string[];
  onChange: (next: string[]) => void;
}

/**
 * Step 02: pick up to 3 genres from a search field, not a chip wall (handoff SPEC §5.2). Typing filters by name
 * or parent, names that START with the query rank first, at most 8 results each showing its parent. Enter adds
 * the highlighted (else top) match and never submits the form. An accessible combobox: arrow keys move the
 * highlight, Escape clears the query.
 */
export function GenrePicker({ value, onChange }: GenrePickerProps) {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const listId = useId();
  const full = value.length >= MAX_GENRES;
  const results = full ? [] : matchGenres(query, value);
  const open = !full && query.trim().length > 0;

  const add = (name: string) => {
    if (full || value.includes(name)) return;
    onChange([...value, name]);
    setQuery("");
    setActive(0);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();   // Enter adds a genre; it must never submit the application
      const pick = results[active] ?? results[0];
      if (pick) add(pick.name);
    } else if (e.key === "ArrowDown" && results.length > 0) {
      e.preventDefault();
      setActive((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp" && results.length > 0) {
      e.preventDefault();
      setActive((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === "Escape") {
      setQuery("");
    }
  };

  const popular = POPULAR_GENRES.filter((g) => !value.includes(g)).slice(0, 8);

  return (
    <div>
      {value.length > 0 && (
        <div className={styles.picked}>
          {value.map((g) => (
            <button
              key={g}
              type="button"
              className={styles.pickedChip}
              aria-label={`Remove ${g}`}
              onClick={() => onChange(value.filter((x) => x !== g))}
            >
              {g}
              <span aria-hidden="true" className={styles.pickedX}>×</span>
            </button>
          ))}
        </div>
      )}
      <div className={styles.searchWrap}>
        <svg className={styles.searchIcon} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <path d="M20 20l-3.5-3.5" />
        </svg>
        <input
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && results[active] ? `${listId}-${active}` : undefined}
          aria-label="Search genres"
          autoComplete="off"
          className={styles.searchInput}
          data-full={full || undefined}
          disabled={full}
          value={query}
          placeholder={full ? "You've picked 3. Remove one to change it." : "Search genres, like shoegaze or metalcore"}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
        />
      </div>
      {open && (
        <div id={listId} role="listbox" className={styles.results}>
          {results.map((r, i) => (
            <button
              key={r.name}
              id={`${listId}-${i}`}
              type="button"
              role="option"
              aria-selected={i === active}
              className={styles.result}
              data-active={i === active || undefined}
              onMouseEnter={() => setActive(i)}
              onClick={() => add(r.name)}
            >
              {r.name}
              <span className={styles.resultParent}>{r.parent.toUpperCase()}</span>
            </button>
          ))}
          {results.length === 0 && (
            <div className={styles.noMatch}>No match. Try a broader genre, like Rock or Electronic.</div>
          )}
        </div>
      )}
      {!open && !full && (
        <div className={styles.popular}>
          <span className={styles.popularLabel}>POPULAR</span>
          {popular.map((g) => (
            <button key={g} type="button" className={styles.popularChip} onClick={() => add(g)}>
              + {g}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
